import styled from 'styled-components';
import { ErrorBanner as EBanner } from 'ui/banner';
import { CheckboxField } from 'lib/iterable-hook-form';
import { Subtitle2 as S2, spacing } from 'ui/theme';
import { useFormContext } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { USERNAME_PASSWORD, GOOGLE_SIGNIN, SSO, AUTH_METHOD_LABELS } from '../constants';
import { useAuthenticationQueryContext } from '../contexts';
import { createUpdatePayload } from '../utils';
import { SSOFields } from './SSOFields';
import { CheckboxFormField } from './CheckboxFormField';
import { SpacedFlexContainer, Description } from './AuthenticationPolicyPage.styled';
import { AuthenticationPolicyFormProvider } from '../contexts/AuthenticationPolicyFormProvider';

const ErrorBanner = styled(EBanner)`
  margin-bottom: ${spacing.plusHalf};
`;

const Subtitle2 = styled(S2)`
  margin-bottom: ${spacing.plusHalf};
`;

const DescriptionWithMargin = styled(Description)`
  margin-bottom: ${spacing.base};
`;

type Props = {
  onCancel: () => void;
};

const BaseAuthenticationPolicyForm = ({ onCancel }: Props) => {
  const { updateOrgAuthMethods, isUpdating } = useAuthenticationQueryContext();

  const { watch, setValue, handleSubmit } = useFormContext();

  const [usernamePassword, googleSignin, sso] = watch([USERNAME_PASSWORD, GOOGLE_SIGNIN, SSO]);

  const anyAuthSelected = [usernamePassword, googleSignin, sso].some(Boolean);

  const onSubmit = (values: Record<string, string | boolean>) => {
    const payload = createUpdatePayload(values);
    updateOrgAuthMethods(payload, { onSuccess: onCancel });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Subtitle2>Authentication methods</Subtitle2>
      {!anyAuthSelected && (
        <ErrorBanner>
          One authentication method is required. Please select an option below or cancel to revert
          to your previous settings.
        </ErrorBanner>
      )}
      <CheckboxFormField
        name={USERNAME_PASSWORD}
        label={AUTH_METHOD_LABELS[USERNAME_PASSWORD]}
        onClick={() => setValue(USERNAME_PASSWORD, !usernamePassword)}
        description="By default users will be able to login with their email address as their username and a password that they set."
      >
        <CheckboxField id={USERNAME_PASSWORD} name={USERNAME_PASSWORD} />
      </CheckboxFormField>
      <CheckboxFormField
        name={GOOGLE_SIGNIN}
        label={AUTH_METHOD_LABELS[GOOGLE_SIGNIN]}
        onClick={() => setValue(GOOGLE_SIGNIN, !googleSignin)}
        description="Requires that users authenticate with Google Sign-In (which means that their email addresses must correspond to a Google account)."
      >
        <CheckboxField id={GOOGLE_SIGNIN} name={GOOGLE_SIGNIN} />
      </CheckboxFormField>
      <CheckboxFormField
        name={SSO}
        label={AUTH_METHOD_LABELS[SSO]}
        onClick={() => setValue(SSO, !sso)}
        description={
          <>
            <DescriptionWithMargin>
              Authenticates users with SAML from a third-party identity provider, such as Okta,
              which can be used to provide single sign-on.
            </DescriptionWithMargin>
            {sso && <SSOFields />}
          </>
        }
      >
        <CheckboxField id={SSO} name={SSO} />
      </CheckboxFormField>

      <SpacedFlexContainer>
        <PrimaryButton type="submit" isDisabled={!anyAuthSelected} isLoading={isUpdating}>
          Save
        </PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </SpacedFlexContainer>
    </form>
  );
};

export const AuthenticationPolicyForm = ({ onCancel }: Props) => (
  <AuthenticationPolicyFormProvider>
    <BaseAuthenticationPolicyForm onCancel={onCancel} />
  </AuthenticationPolicyFormProvider>
);
